
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrdersQuery } from "./useOrdersManagement/ordersQuery";
import { useOrderOperations } from "./useOrdersManagement/orderOperations";
import { useConnectionTest } from "./useOrdersManagement/connectionTest";

export const useOrdersManagement = (shouldFetchData: boolean, isAuthReady: boolean) => {
  const queryClient = useQueryClient();

  // Clear or refetch orders based on authentication state
  useEffect(() => {
    if (shouldFetchData && isAuthReady) {
      console.log('🔄 Authentication ready, triggering orders refetch...');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    }
  }, [shouldFetchData, isAuthReady, queryClient]);

  // Listen for localStorage changes (like SMS auth changes)
  useEffect(() => {
    const handleAuthChange = () => {
      const staffAuth = localStorage.getItem('staff_sms_auth');
      const smsAdminAuth = localStorage.getItem('sms_admin_auth');
      const bypass = localStorage.getItem('location_bypass');
      
      if (staffAuth || smsAdminAuth || bypass) {
        console.log('🔄 Auth detected, refreshing orders...');
        queryClient.invalidateQueries({ queryKey: ['orders'] });
      }
    };

    window.addEventListener('storage', handleAuthChange);
    return () => window.removeEventListener('storage', handleAuthChange);
  }, [queryClient]);

  const { data: orders = [], isLoading: ordersLoading, error: ordersError } = useOrdersQuery(shouldFetchData, isAuthReady);
  const { handleOrderCheckIn } = useOrderOperations();
  const { testSupabaseConnection } = useConnectionTest();

  // Log when orders data changes
  useEffect(() => {
    console.log('📊 Orders data updated:', {
      count: orders?.length || 0,
      orders: orders?.map(o => ({ id: o.id, supplier: o.supplierName })) || []
    });
  }, [orders]);

  return {
    orders,
    ordersLoading,
    ordersError,
    handleOrderCheckIn,
    testSupabaseConnection
  };
};

// Re-export types for backward compatibility
export type { Order, OrderCheckInData } from "./useOrdersManagement/types";
